import { call, put } from "typed-redux-saga";
import { AuthLoginResponse } from "../../core/api/requests/auth/login/types";
import {
  forgotPasswordApi,
  loginApi,
  logoutApi,
  resetPasswordApi,
} from "../../core/http/httpClient";
import { ApiResponse } from "../../core/types";
import { takeEveryFsa } from "../operations";
import { authActions } from "./actions";
import { LocalStorageManager } from "../../core/storage/LocalStorageManager";
import { AppConfig } from "../../AppConfig";
import { makeAsyncApiCall } from "../common";
import { User } from "./types";

export function* authSaga() {
  yield takeEveryFsa(authActions.login.started, function* (action) {
    const { onComplete, onError, onSuccess, ...body } = action.payload;
    const { response, error }: ApiResponse<AuthLoginResponse> = yield call(
      () => {
        return loginApi
          .call(body)
          .then((response) => ({ response }))
          .catch((error) => ({ error }));
      }
    );

    onComplete?.();

    if (!response || error) return onError?.(error);

    LocalStorageManager.Instance.saveObject(
      AppConfig.Instance.LocalStorageKey["accessToken"],
      response.data.access
    );

    // Store refresh token
    LocalStorageManager.Instance.saveObject(
      AppConfig.Instance.LocalStorageKey["refreshToken"],
      response.data.refresh
    );

    // Store user data from login response
    const userData: User = {
      id: response.data.user.id.toString(),
      username: response.data.user.username,
      email: response.data.user.email,
      role: response.data.user.role,
      last_login: response.data.user.last_login,
    };

    LocalStorageManager.Instance.saveObject(
      AppConfig.Instance.LocalStorageKey["loggedInUser"],
      userData
    );

    yield* put(
      authActions.login.done({
        params: action.payload,
        result: response.data,
      })
    );
    onSuccess?.(response.data);

    // Dispatch getUser to update the state with user data
    yield* put(authActions.getUser.done({
      params: {},
      result: userData,
    }));
  });

  yield takeEveryFsa(authActions.getUser.started, function* (action) {
    const loggedInUser: User | null = LocalStorageManager.Instance.getObject(
      AppConfig.Instance.LocalStorageKey["loggedInUser"]
    );

    if (loggedInUser) {
      yield* put(
        authActions.getUser.done({
          params: action.payload,
          result: loggedInUser,
        })
      );
    }

    // Mark as initialized regardless of whether user data was found
    yield* put(authActions.setIsInitialized(true));
  });

  yield takeEveryFsa(authActions.forgotPassword.started, function* (action) {
    const { onComplete, onError, onSuccess, ...body } = action.payload;
    const { response, error }: ApiResponse<{}> = yield call(() =>
      forgotPasswordApi
        .call(body)
        .then((response) => ({ response }))
        .catch((error) => ({ error }))
    );

    onComplete?.();

    if (!response || error) return onError?.(error);

    yield* put(
      authActions.forgotPassword.done({
        params: action.payload,
        result: response.data,
      })
    );
    onSuccess?.(response.data);
  });

  yield takeEveryFsa(authActions.resetPassword.started, function* (action) {
    const { onComplete, onError, onSuccess, ...body } = action.payload;
    const { response, error }: ApiResponse<{}> = yield call(() =>
      resetPasswordApi
        .call(body)
        .then((response) => ({ response }))
        .catch((error) => ({ error }))
    );

    onComplete?.();

    if (!response || error) return onError?.(error);

    yield* put(
      authActions.resetPassword.done({
        params: action.payload,
        result: response.data,
      })
    );
    onSuccess?.(response.data);
  });

  yield takeEveryFsa(authActions.logout.started, function* (action) {
    yield* makeAsyncApiCall(action, authActions.logout, logoutApi);
  });
}
